import { apiService } from './api';

const API = '/business-days';

export interface TenderTotalDTO {
  method: string;
  amount: number;
  count:  number;
}

export interface BusinessDayDTO {
  id:            number;
  outletId:      number;
  outletName:    string;
  businessDate:  string;
  status:        'Open' | 'Closed';
  dayEndNumber:  number | null;
  openedAt:      string;
  openedByName:  string | null;
  closedAt:      string | null;
  closedByName:  string | null;

  grossSales:    number;
  discountTotal: number;
  taxTotal:      number;
  netSales:      number;
  invoiceCount:  number;
  cashVariance:  number;

  tenderBreakdown: TenderTotalDTO[];

  notes:        string | null;
  reopenReason: string | null;

  /** True when this open day is past its outlet's close cutoff — the POS must block new
   *  sales and prompt day-end. Always false for closed days / outlets with no cutoff. */
  requiresDayEnd: boolean;
}

/** Machine-readable blocker codes, so the UI can route the user to the fix. */
export type DayEndBlockerCode =
  | 'NoOpenDay'
  | 'OpenTill'
  | 'UnsettledInvoice'
  | 'OpenRestaurantOrder';

export interface DayEndBlockerDTO {
  code:        DayEndBlockerCode;
  description: string;
  referenceId: number | null;
  reference:   string | null;
}

export interface TillSessionSummaryDTO {
  id:             number;
  sessionNumber:  string;
  cashierName:    string;
  status:         'Open' | 'Closed';
  openedAt:       string;
  closedAt:       string | null;
  openingBalance: number;
  variance:       number | null;
}

export interface DayEndPreviewDTO {
  businessDay:  BusinessDayDTO | null;
  blockers:     DayEndBlockerDTO[];
  tillSessions: TillSessionSummaryDTO[];
  canClose:     boolean;
}

/** Minimal stale-day status for the caller's own outlet — usable on the cashier POS. */
export interface CurrentDayStatusDTO {
  hasOpenDay:     boolean;
  businessDate:   string | null;
  requiresDayEnd: boolean;
}

export const businessDayService = {
  getCurrent: (outletId: number) =>
    apiService.get<BusinessDayDTO>(`${API}/current`, { outletId }),

  /** Resolves the caller's outlet server-side; no outletId or DayEnd permission needed. */
  getCurrentStatus: () =>
    apiService.get<CurrentDayStatusDTO>(`${API}/current-status`),

  getPreview: (outletId: number) =>
    apiService.get<DayEndPreviewDTO>(`${API}/preview`, { outletId }),

  getHistory: (outletId: number, pageNumber = 1, pageSize = 30) =>
    apiService.get<BusinessDayDTO[]>(`${API}/history`, { outletId, pageNumber, pageSize }),

  getById: (id: number | string) =>
    apiService.get<BusinessDayDTO>(`${API}/${id}`),

  open: (outletId: number, businessDate?: string) =>
    apiService.post<BusinessDayDTO>(`${API}/open`, { outletId, businessDate }),

  close: (outletId: number, notes?: string) =>
    apiService.post<BusinessDayDTO>(`${API}/close`, { outletId, notes }),

  reopen: (id: number, reason: string) =>
    apiService.post<BusinessDayDTO>(`${API}/${id}/reopen`, { reason }),
};
